export const MOBILE_AUTH_SESSION_MAX_AGE_MS = 30 * 60 * 1000;

export const MOBILE_AUTH_SESSION_KEYS = {
    login: 'mobile_login',
    redirectUri: 'mobile_redirect_uri',
    appType: 'mobile_app_type',
    email: 'mobile_email',
    codeChallenge: 'mobile_code_challenge',
    startedAtMs: 'mobile_auth_started_at_ms',
} as const;

const RETRYABLE_MOBILE_AUTH_ERRORS = new Set([
    'login_failed',
    'invalid_session',
    'user_not_found',
    'invalid_email',
    'auth_error',
]);

const parseStartedAtMs = (rawValue: string | null): number | null => {
    if (!rawValue) {
        return null;
    }

    const startedAtMs = Number.parseInt(rawValue, 10);
    if (Number.isNaN(startedAtMs)) {
        return null;
    }

    return startedAtMs;
};

export const hasFreshMobileAuthSession = (
    getItem: (key: string) => string | null,
    nowMs: number = Date.now(),
): boolean => {
    const hasMobileLogin = getItem(MOBILE_AUTH_SESSION_KEYS.login) === 'true';
    const mobileRedirectUri = getItem(MOBILE_AUTH_SESSION_KEYS.redirectUri);
    if (!hasMobileLogin || !mobileRedirectUri) {
        return false;
    }

    const startedAtMs = parseStartedAtMs(getItem(MOBILE_AUTH_SESSION_KEYS.startedAtMs));
    if (startedAtMs === null) {
        return false;
    }

    const ageMs = nowMs - startedAtMs;
    return ageMs >= 0 && ageMs <= MOBILE_AUTH_SESSION_MAX_AGE_MS;
};

export const hasLegacyMobileAuthSessionWithoutTimestamp = (
    getItem: (key: string) => string | null,
): boolean => {
    const hasMobileLogin = getItem(MOBILE_AUTH_SESSION_KEYS.login) === 'true';
    const mobileRedirectUri = getItem(MOBILE_AUTH_SESSION_KEYS.redirectUri);
    const startedAtMs = parseStartedAtMs(getItem(MOBILE_AUTH_SESSION_KEYS.startedAtMs));

    return hasMobileLogin && !!mobileRedirectUri && startedAtMs === null;
};

export const shouldPreserveFreshMobileAuthSessionForLoginError = ({
    isMobileLogin,
    authError,
    getItem,
    nowMs = Date.now(),
}: {
    isMobileLogin: boolean;
    authError: string | null;
    getItem: (key: string) => string | null;
    nowMs?: number;
}): boolean => {
    const normalizedError = (authError || '').trim().toLowerCase();
    if (isMobileLogin || !normalizedError || !RETRYABLE_MOBILE_AUTH_ERRORS.has(normalizedError)) {
        return false;
    }

    return hasFreshMobileAuthSession(getItem, nowMs);
};

export const clearMobileAuthSession = (removeItem: (key: string) => void): void => {
    removeItem(MOBILE_AUTH_SESSION_KEYS.login);
    removeItem(MOBILE_AUTH_SESSION_KEYS.redirectUri);
    removeItem(MOBILE_AUTH_SESSION_KEYS.appType);
    removeItem(MOBILE_AUTH_SESSION_KEYS.email);
    removeItem(MOBILE_AUTH_SESSION_KEYS.codeChallenge);
    removeItem(MOBILE_AUTH_SESSION_KEYS.startedAtMs);
};
